import { AccessCheck } from '@particles/index';
import { useQuery } from '@tanstack/react-query';
import { Component, Fragment } from 'react';
import { roundHalfUp } from "@utils/roundHalfUp";
import { LineItemUnitTypes, PriceBookItemTypes } from '../utils/enums';
import { formatServiceDates, getLineItemUnits, valueIsDefined } from "../utils/utils";
import Banner from './Banner';


export async function fetchPriceBookData(currentUser, description) {
    if (valueIsDefined(currentUser?.id)) {
        if (valueIsDefined(description)) {
            // Attempt to find a matching item in the PriceBook
            let params = new URLSearchParams()
            params.append("confirmed", true)
            params.append("description", description)

            const priceBookEndpoint = DjangoUrls["pricebook:api-pricebookitem-lightweight-list"](currentUser.service_company.slug) + `?${params}`
            const priceBookResponse = await fetch(priceBookEndpoint)

            if (!priceBookResponse.ok) {
                return null
            }

            const priceBookResponseJSON = await priceBookResponse.json()

            if (priceBookResponseJSON.results.length !== 0) {
                return priceBookResponseJSON.results[0]
            }
        }
    } else {
        if (valueIsDefined(window.PRICEBOOK_PART_DATA_MAP)) {
            const priceBookItem = window.PRICEBOOK_PART_DATA_MAP?.[description]
            return priceBookItem ?? null
        }
    }

    return null
}

function PriceBookPartDataDisplay(props) {
    const { lineItem } = props
    const currentUser = window.CURRENT_USER

    const { data: pricebookItem, error, isLoading, isError } = useQuery({
        queryKey: ["pricebook-retrieve-by-description", currentUser?.id, lineItem.description],
        queryFn: () => fetchPriceBookData(currentUser, lineItem.description),
        staleTime: 60000,
    })


    if (isError) {
        console.error(error)
    } else if (pricebookItem || lineItem.description) {
        return (
            <div className="line-item-display__row pricebook_part_data_display">
                <div className="line-item-display__field" aria-label="Manufacturer">
                    <div className="line-item-display__field-label" aria-hidden="true">Manufacturer</div>
                    <div className="line-item-display__field-value" aria-label="Manufacturer">{pricebookItem?.manufacturer || "\u2014"}</div>
                </div>
                <div className="line-item-display__field" aria-label="Part Number">
                    <div className="line-item-display__field-label" aria-hidden="true">Manufacturer Part #</div>
                    <div className="line-item-display__field-value" aria-label="Part Number">{pricebookItem?.part_number || "\u2014"}</div>
                </div>
            </div>
        )
    }

}

function LineItemWarningBanner({ description }) {
    const currentUser = window.CURRENT_USER;
    const {
        data: pricebookItem,
        error,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["pricebook-retrieve-by-description", currentUser.id, description],
        queryFn: () => fetchPriceBookData(currentUser, description),
        staleTime: 60000,
    });

    if (isError) {
        console.error(error);
        return null;
    }

    if (isLoading) {
        return null;
    }

    if (!pricebookItem && description) {
        return (
            <div className="line-item-display__row">
                <span>
                    <Banner
                        type="info"
                        text="This line item was manually created outside your standard pricebook. Custom items may lack verified pricing, costs, and detailed specifications."
                    />
                </span>
            </div>
        );
    }

    return null;
}

class LineItemListDisplay extends Component {

    render() {
        const {
            fieldName,
            fieldLabel,
            parent,
            lineItemList,
            emptyMessage,
            formatCurrencyValue,
            useTaxes=false,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
            isBillLineItem=false,
            isCreditNoteLineItem=false,
            parentLineItems=[],
            parentLineItemFieldName="",
            parentLineItemLabel="",
            isShownToClients=false,
            showPricebookPartDisplay=false,
            hasSubtypeChoices=false,
        } = this.props

        return (
            <div className="data-panel__form__field" id={`div_id_${fieldName}`} aria-label={fieldLabel}>
                <span className="data-panel__form__field__label" aria-hidden="true">{fieldLabel}</span>
                <div className="data-panel__form__field__display-list" aria-label={`${fieldLabel} List`}>
                    {
                        lineItemList.length === 0
                        ?
                        `(${emptyMessage})`
                        :
                        lineItemList.map((item, index) => {
                            const { formattedDates, formattedLabel } = formatServiceDates(item.service_start_date, item.service_end_date, "UTC")
                            const { quantityUnit, priceUnit, priceLabel } = getLineItemUnits(item)

                            return (
                                <div key={`${fieldName}_${index}`} className="data-panel__form__field__display-list__object line-item-display" aria-label="Line Item">
                                    {
                                        !(window.USING_PUBLIC_URL === true) && (
                                            <LineItemWarningBanner description={item.description} />
                                        )
                                    }
                                    <div className="line-item-display__row">
                                        <div className="line-item-display__field" aria-label="Short Description">
                                            <div className="line-item-display__field-label" aria-hidden="true">Short Description</div>
                                            <div className="line-item-display__field-value" aria-label="Short Description">{item.description}</div>
                                        </div>
                                    </div>
                                    { (window.MARKETPLACE_ENTITY_SLUG === parent.service_company.slug || isPurchaseOrderLineItem) && item.line_item_type === PriceBookItemTypes.part && showPricebookPartDisplay && <PriceBookPartDataDisplay lineItem={item} /> }
                                    {
                                        hasSubtypeChoices && !(window.USING_PUBLIC_URL === true) && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field" aria-label="Subtype">
                                                    <div className="line-item-display__field-label" aria-hidden="true">Subtype</div>
                                                    <div className="line-item-display__field-value" aria-label="Subtype">{item.subtype ? item.subtype_label : "\u2014"}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        !isEstimateLineItem && !isCreditNoteLineItem && item.line_item_type === PriceBookItemTypes.service && (isJobLineItem ? !parent.is_job_walk : true) && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field" aria-label={formattedLabel}>
                                                    <div className="line-item-display__field-label" aria-hidden="true">{formattedLabel}</div>
                                                    <div className="line-item-display__field-value" aria-label={formattedLabel}>{formattedDates}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        window.MARKETPLACE_ENTITY_SLUG === parent.service_company.slug && item.inventory_location && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field" aria-label="Inventory Location">
                                                    <div className="line-item-display__field-label" aria-hidden="true">Inventory Location</div>
                                                    <div className="line-item-display__field-value" aria-label="Inventory Location">{item.inventory_location_label}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        parentLineItemFieldName && item[parentLineItemFieldName] && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field" aria-label={parentLineItemLabel}>
                                                    <div className="line-item-display__field-label" aria-hidden="true">{parentLineItemLabel}</div>
                                                    <div className="line-item-display__field-value" aria-label={parentLineItemLabel}>
                                                        {(() => {
                                                            const parentItem = parentLineItems.find(parent => String(parent.id) === String(item[parentLineItemFieldName]))
                                                            return parentItem ? parentItem.__str__ : item[parentLineItemFieldName]
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        isCreditNoteLineItem && item.line_item_type === PriceBookItemTypes.part && item.return_to_inventory && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field" aria-label="Return to Inventory">
                                                    <div className="line-item-display__field-label" aria-hidden="true">Return to Inventory?</div>
                                                    <div className="line-item-display__field-value" aria-label="Return to Inventory">Yes</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    <div className="line-item-display__row">
                                        {
                                            window.MARKETPLACE_ENTITY_SLUG === parent.service_company.slug && !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (item.line_item_type === PriceBookItemTypes.part || item.line_item_type === PriceBookItemTypes.service || item.line_item_type === PriceBookItemTypes.other) && !isShownToClients && !isPurchaseOrderLineItem && !isBillLineItem && !isCreditNoteLineItem && (
                                                <div className="line-item-display__field" aria-label="Cost">
                                                    <div className="line-item-display__field-label" aria-hidden="true">{`${(item.line_item_type === PriceBookItemTypes.service && item.unit_type === LineItemUnitTypes.hourly) ? "Hourly Cost" : "Unit Cost"}`}</div>
                                                    <div className="line-item-display__field-value" aria-label="Cost">{valueIsDefined(item.cost) ? `${formatCurrencyValue(item.cost).concat((item.line_item_type === PriceBookItemTypes.service && item.unit_type === LineItemUnitTypes.hourly) ? priceUnit:'')}` : "--" }</div>
                                                </div>
                                            )
                                        }
                                        <div className="line-item-display__field" aria-label="Quantity">
                                            <div className="line-item-display__field-label" aria-hidden="true">Qty.</div>
                                            <div className="line-item-display__field-value" aria-label="Quantity">{valueIsDefined(item.quantity) ? `${parseFloat(item.quantity)}${quantityUnit}` : "--"}</div>
                                        </div>
                                        {
                                            isPurchaseOrderLineItem
                                            ?
                                            <Fragment>
                                                <div className="line-item-display__field" aria-label="Expected Vendor Cost">
                                                    <div className="line-item-display__field-label" aria-hidden="true">Expected Vendor Cost</div>
                                                    <div className="line-item-display__field-value" aria-label="Expected Vendor Cost">{valueIsDefined(item.expected_cost) ? formatCurrencyValue(item.expected_cost) : "--"}</div>
                                                </div>
                                                <div className="line-item-display__field" aria-label="Amount">
                                                    <div className="line-item-display__field-label" aria-hidden="true">Amount</div>
                                                    <div className="line-item-display__field-value" aria-label="Amount">{valueIsDefined(item.quantity) && valueIsDefined(item.expected_cost) ? formatCurrencyValue(roundHalfUp(parseFloat(item.quantity) * parseFloat(item.expected_cost), 2)) : "--"}</div>
                                                </div>
                                            </Fragment>
                                            :
                                                !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (
                                                    <Fragment>
                                                        <div className="line-item-display__field" aria-label="Price">
                                                            <div className="line-item-display__field-label" aria-hidden="true">{isBillLineItem ? "Cost" : priceLabel}</div>
                                                            <div className="line-item-display__field-value" aria-label="Price">{valueIsDefined(item.price) ? `${formatCurrencyValue(item.price)}${priceUnit}` : "--"}</div>
                                                        </div>
                                                        <div className="line-item-display__field" aria-label="Amount">
                                                            <div className="line-item-display__field-label" aria-hidden="true">Amount</div>
                                                            <div className="line-item-display__field-value" aria-label="Amount">{valueIsDefined(item.quantity) && valueIsDefined(item.price) ? formatCurrencyValue(roundHalfUp(parseFloat(item.quantity) * parseFloat(item.price), 2)) : "--"}</div>
                                                        </div>
                                                    </Fragment>
                                                )
                                        }
                                    </div>
                                    {
                                        isPurchaseOrderLineItem && (
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Vendor Part #</div>
                                                    <div className="line-item-label__field-value">{item.part_number}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        useTaxes && item.line_item_type !== PriceBookItemTypes.discount && !isBillLineItem && (
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Tax</div>
                                                    <div className="line-item-label__field-value">{<span className="data-tag" style={{margin: "0", width: "auto"}}>{item.is_taxable ? "Taxable" : "Non-Taxable"}</span>}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        item.summary && (
                                            <div className="line-item-display__row">
                                                <div className="line-item-display__field">
                                                    <div className="line-item-display__field-label">{item.line_item_type === PriceBookItemTypes.service ? "Service" : "Detailed"} Summary</div>
                                                    <div className="line-item-display__field-value preserve-whitespace">{item.summary}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        !(window.USING_PUBLIC_URL === true) && isPurchaseOrderLineItem && valueIsDefined(item.estimates) && item.estimates.length !== 0 && (
                                            <AccessCheck entitlements={["entitlement_estimates_enabled"]}>
                                                <div className="line-item-label__row">
                                                    <div className="line-item-label__field">
                                                        <div className="line-item-label__field-label">Associated Estimates</div>
                                                        <div className="line-item-label__field-value">{item.estimates_label}</div>
                                                    </div>
                                                </div>
                                            </AccessCheck>
                                        )
                                    }
                                    {
                                        !(window.USING_PUBLIC_URL === true) && isPurchaseOrderLineItem && valueIsDefined(item.jobs) && item.jobs.length !== 0 && (
                                            <AccessCheck entitlements={["entitlement_jobs_enabled"]}>
                                                <div className="line-item-label__row">
                                                    <div className="line-item-label__field">
                                                        <div className="line-item-label__field-label">Associated Jobs</div>
                                                        <div className="line-item-label__field-value">{item.jobs_label}</div>
                                                    </div>
                                                </div>
                                            </AccessCheck>
                                        )
                                    }
                                    {
                                        !(window.USING_PUBLIC_URL === true) && isPurchaseOrderLineItem && valueIsDefined(item.invoices) && item.invoices.length !== 0 && (
                                            <AccessCheck entitlements={["entitlement_invoices_enabled"]}>
                                                <div className="line-item-label__row">
                                                    <div className="line-item-label__field">
                                                        <div className="line-item-label__field-label">Associated Invoices</div>
                                                        <div className="line-item-label__field-value">{item.invoices_label}</div>
                                                    </div>
                                                </div>
                                            </AccessCheck>
                                        )
                                    }
                                    {
                                        valueIsDefined(item.equipment_display_name) && (
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Equipment</div>
                                                    <div className="line-item-label__field-value">{item.equipment_display_name}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        valueIsDefined(item.user_display_name) && (
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Technician</div>
                                                    <div className="line-item-label__field-value">{item.user_display_name}</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        })
                    }
                </div>
            </div>
        )
    }
}

export default LineItemListDisplay;
